import { type ApiClient, type FileShareClient } from "@anna-money/anna-web-lib/api";
import { AnnaError } from "@anna-money/anna-web-lib/utils";
import { makeAutoObservable } from "mobx";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import {
  type AsicNominationEvidenceContext,
  buildAsicNominationEvidence,
} from "services/asicAgentNomination/asicNominationEvidence";

const SIGNATURE_FILE_NAME = "asic-agent-signature.png";
const SIGNATURE_MIME_TYPE = "image/png";

type BuildNominationEvidence = typeof buildAsicNominationEvidence;

export class AsicAgentSignatureStore {
  constructor(
    private readonly _fileShareClient: FileShareClient,
    private readonly _apiClient: ApiClient,
    private readonly _analyticsManager: AnalyticsManager,
    // Injected so the canvas-based composition can be substituted in tests,
    // where jsdom has no canvas.
    private readonly _buildEvidence: BuildNominationEvidence = buildAsicNominationEvidence,
  ) {
    makeAutoObservable(this, { _buildEvidence: false } as any);
  }

  /**
   * Composes the nomination evidence image (the statement, company and signed-at
   * time rendered around the customer's signature) from the signature data URL
   * the pad produced, turns it into a PNG file, uploads it to the file share
   * service and returns the resulting file id. Uploading the whole rendered
   * statement — not the signature alone — keeps the record showing what was
   * signed. Throws if the upload fails so the submit flow can surface the error.
   */
  async uploadNominationEvidence(signatureDataUrl: string, context: AsicNominationEvidenceContext): Promise<string> {
    try {
      const evidenceDataUrl = await this._buildEvidence(signatureDataUrl, context);
      const file = dataUrlToFile(evidenceDataUrl, SIGNATURE_FILE_NAME, SIGNATURE_MIME_TYPE);
      const uploaded = await this._fileShareClient.uploadFile(file);
      this._analyticsManager.event("asic-agent-signature-uploaded", { file: uploaded.id });
      return uploaded.id;
    } catch (e) {
      console.error(e);
      this._analyticsManager.event("fail-asic-agent-signature");
      throw e;
    }
  }

  /**
   * Downloads the previously captured ASIC agent signature so it can be shown
   * back to the user. Customers cannot read from file-share directly, so this
   * goes through the formations backend, which resolves the company's signature
   * file id and streams the image with its service-level file-share access.
   */
  async downloadSignature(companyId: string): Promise<Blob> {
    return await this._apiClient.downloadFile(`api/formations/companies/${companyId}/asic-agent-signature`);
  }
}

function dataUrlToFile(dataUrl: string, fileName: string, mimeType: string): File {
  if (!dataUrl.startsWith("data:")) {
    throw new AnnaError("Signature is not a valid data URL");
  }
  const base64 = dataUrl.split(",")[1] ?? "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mimeType });
}
